import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { NotFound, Skeleton } from '@beamx/shared';
import { ApiKeyProvider, useApiKey } from './lib/auth/ApiKeyContext.js';
import { CustomerShell } from './components/CustomerShell.js';
import { ApiKeyPromptPage } from './pages/ApiKeyPromptPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { SenderDomainsListPage } from './pages/SenderDomainsListPage.js';
import { TemplatesListPage } from './pages/TemplatesListPage.js';
import { ActivityPage } from './pages/ActivityPage.js';
import { MessageDetailPage } from './pages/MessageDetailPage.js';
import { WebhooksPage } from './pages/WebhooksPage.js';
import { SuppressionPage } from './pages/SuppressionPage.js';

// Code-split the CodeMirror-backed pages so the editor (~580 KB) loads on demand
// instead of bloating the main bundle.
const ComposePage = lazy(() => import('./pages/ComposePage.js').then((m) => ({ default: m.ComposePage })));
const TemplateEditorPage = lazy(() =>
  import('./pages/TemplateEditorPage.js').then((m) => ({ default: m.TemplateEditorPage })),
);

/**
 * Guard that bounces to the API-key prompt when the tenant hasn't connected yet.
 * Until Phase 13 self-serve signup, "logged in" === "we have an API key in localStorage".
 */
function RequireApiKey({ children }: { children: React.ReactNode }) {
  const { apiKey } = useApiKey();
  if (!apiKey) return <Navigate to="/connect" replace />;
  return <>{children}</>;
}

/** Guarded + inside the customer app shell, with a Suspense boundary for lazy pages. */
const shell = (el: ReactElement) => (
  <RequireApiKey>
    <CustomerShell>
      <Suspense fallback={<Skeleton className="h-96 w-full" />}>{el}</Suspense>
    </CustomerShell>
  </RequireApiKey>
);

const router = createBrowserRouter([
  { path: '/connect', element: <ApiKeyPromptPage /> },
  { path: '/', element: shell(<DashboardPage />) },
  { path: '/compose', element: shell(<ComposePage />) },
  { path: '/sender-domains', element: shell(<SenderDomainsListPage />) },
  { path: '/templates', element: shell(<TemplatesListPage />) },
  { path: '/templates/new', element: shell(<TemplateEditorPage />) },
  { path: '/templates/:id/edit', element: shell(<TemplateEditorPage />) },
  { path: '/activity', element: shell(<ActivityPage />) },
  { path: '/messages/:id', element: shell(<MessageDetailPage />) },
  { path: '/webhooks', element: shell(<WebhooksPage />) },
  { path: '/suppression', element: shell(<SuppressionPage />) },
  { path: '*', element: shell(<NotFound />) },
]);

export function App() {
  return (
    <ApiKeyProvider>
      <RouterProvider router={router} />
    </ApiKeyProvider>
  );
}
